"use client";

import { useEffect, useState, type RefObject } from "react";

// Row windowing for long tables: render only the slice that's on screen, and reserve the rest of
// the height with two spacer rows. Keeps the DOM at ~40 <tr>s instead of tens of thousands, so
// scrolling stays smooth and re-renders stay cheap.
//
// Deliberately dependency-free and <table>-friendly: it returns padding heights for a leading and
// trailing spacer <tr>, which preserves table semantics and column widths (unlike absolute
// positioning, which breaks them).
//
// It is a no-op below `enabled` — small lists render exactly as they did before, so there is no
// behaviour change (and no fixed-height requirement) until a list is actually big.

interface Options {
  count: number;
  rowHeight: number;   // must match the row's rendered height for the maths to line up
  overscan?: number;   // extra rows above/below the viewport, so fast scrolls don't flash blanks
  enabled?: boolean;
}

export interface VirtualWindow {
  start: number;
  end: number;
  topPad: number;
  bottomPad: number;
  virtualized: boolean;
}

export function useVirtualRows(
  scrollRef: RefObject<HTMLElement | null>,
  { count, rowHeight, overscan = 10, enabled = true }: Options
): VirtualWindow {
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !enabled) return;

    const onScroll = () => setScrollTop(el.scrollTop);
    const measure = () => setViewport(el.clientHeight);

    measure();
    setScrollTop(el.scrollTop);

    el.addEventListener("scroll", onScroll, { passive: true });
    // Viewport height changes with window resize / sidebar collapse — re-measure rather than
    // assume a fixed height.
    const ro = new ResizeObserver(measure);
    ro.observe(el);

    return () => {
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
    };
  }, [scrollRef, enabled, count]);

  if (!enabled || count === 0) {
    return { start: 0, end: count, topPad: 0, bottomPad: 0, virtualized: false };
  }

  // Fall back to a sane viewport on the very first paint (before measure() has run).
  const visibleRows = Math.ceil((viewport || 600) / rowHeight);
  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const end = Math.min(count, start + visibleRows + overscan * 2);

  return {
    start,
    end,
    topPad: start * rowHeight,
    bottomPad: Math.max(0, (count - end) * rowHeight),
    virtualized: true
  };
}
